<template>
  <transition name="fade" mode="out-in">
    <div class="col-12 text-center mt-5" v-if="success">
      <div class="title">
        <h3 class="-upper">{{ $t('Thank you') }}</h3>
      </div>
      <div class="form-heading">
        {{ $t('Your message has been sent successfully') }}
      </div>
    </div>
    <form
      v-else
      id="contact-form"
      aria-labelledby="contact-form-btn"
      class="form text-left"
      @submit.prevent="register"
      novalidate
    >
      <div class="title">
        <h3 class="-upper">{{ $t('Contact the boutique') }}</h3>
      </div>

      <slot></slot>

      <div class="form-heading">{{ $t('How can we help?') }}</div>

      <base-select
        id="country"
        name="country"
        :options="countries"
        :placeholder="$t('Country/Region') + ' *'"
        v-model="country"
        :required="true"
        autocomplete="country-name"
        :validations="[
          {
            condition: !$v.country.required && $v.country.$error,
            text: $t('Field is required.')
          }
        ]"
      />

      <base-select
        v-if="this.storeOptions && this.storeOptions.length"
        id="store"
        name="store"
        v-model="store"
        :placeholder="$t('Choose a store') + ' *'"
        :options="this.storeOptions"
        :validations="[
          {
            condition: $v.store.$error && !$v.store.required,
            text: $t('Field is required.')
          }
        ]"
        :required="true"
      />
      <div class="col-12" v-else-if="country">
        <div class="error-msg">
          {{
            $t('No official boutiques are available in the selected country')
          }}
        </div>
      </div>

      <base-select
        id="reason"
        name="reason"
        v-model="reason"
        :placeholder="$t('How can we help?') + ' *'"
        :options="[
          { value: 'appointment', label: $t('Book an appointment') },
          { value: 'repair', label: $t('Repair my Zenith') },
          { value: 'information', label: $t('Product information') }
        ]"
        :validations="[
          {
            condition: $v.reason.$error && !$v.reason.required,
            text: $t('Field is required.')
          }
        ]"
        :required="true"
      />

      <div class="row" v-show="reason === 'appointment'">
        <div class="col-12">
          <base-radiobutton
            id="appointment"
            :inline="true"
            name="appointment"
            v-model="appointment"
            :options="[
              {
                label: $t('Boutique appointment'),
                value: 'boutique',
                id: 'radio-boutique'
              },
              {
                label: $t('Virtual appointment'),
                value: 'video',
                id: 'radio-virtual'
              }
            ]"
          />
        </div>
        <div
          class="hours col-12"
          v-if="selectedStore && selectedStore.opening.length"
        >
          <button
            id="opening-hours-btn"
            class="opening-hours-btn"
            type="button"
            :aria-expanded="toggleOpeningHour ? 'true' : 'false'"
            aria-controls="opening-hours"
            @click="toggleOpeningHour = !toggleOpeningHour"
          >
            {{ $t('Opening hours') }}
          </button>
          <ul
            id="opening-hours"
            class="opening-hours"
            aria-labelledby="opening-hours-btn"
            :class="{ '-open': toggleOpeningHour }"
          >
            <li v-for="(days, keyDays) in selectedStore.opening" :key="keyDays">
              <strong class="day">{{
                days.day === 'thrusday' ? $t('thursday') : $t(days.day)
              }}</strong>
              <template v-if="days.slots">
                <span v-for="(hours, keyHours) in days.slots" :key="keyHours"
                  >{{ hours.from }} - {{ hours.to }}</span
                >
              </template>
              <span v-else>{{ $t('Close') }}</span>
            </li>
          </ul>
        </div>
        <div class="col-12" v-else>
          <div class="no-openings">
            {{ $t('The selected store has no openings slots') }}
          </div>
        </div>
        <div class="col-6">
          <base-input
            type="date"
            id="date"
            name="date"
            v-model.lazy="date"
            @blur.native="$v.date.$touch()"
            :placeholder="$t('Date')"
            :validations="[
              {
                condition: !$v.date.sameAs && $v.date.$error,
                text: $t('The shop is closed on the selected day')
              }
            ]"
          />
        </div>
        <div class="col-6">
          <base-select
            id="when"
            name="when"
            v-model="when"
            :placeholder="$t('When')"
            :options="[
              { value: 'Morning', label: $t('Morning') },
              { value: 'Noon', label: $t('Noon') },
              { value: 'Afternoon', label: $t('Afternoon') },
              { value: 'Evening', label: $t('Evening') }
            ]"
          />
        </div>
      </div>

      <base-textarea
        id="message"
        type="text"
        name="message"
        v-model="message"
        :placeholder="$t('Tell us more about your expectation')"
      />

      <div class="form-heading">{{ $t('How should we get back to you?') }}</div>
      <base-radiobutton
        id="getback"
        :inline="true"
        name="getback"
        v-model="getback"
        :options="[
          { label: $t('Call me'), value: 'call', id: 'radio-call' },
          { label: $t('Email me'), value: 'mail', id: 'radio-mail' }
        ]"
      />

      <base-input
        id="email"
        type="email"
        name="email"
        autocomplete="email"
        v-model="email"
        @blur="$v.email.$touch()"
        :placeholder="$t('E-mail address') + ' *'"
        :validations="[
          {
            condition: !$v.email.required && $v.email.$error,
            text: $t('Field is required.')
          },
          {
            condition: !$v.email.email && $v.email.$error,
            text: $t('Please provide valid e-mail address.')
          }
        ]"
        :required="true"
      />
      <base-phone
        id="phone"
        type="text"
        name="phone"
        autocomplete="tel-national"
        v-model="phone"
        :phoneIndic="phoneIndic"
        @phoneIndic="
          v => {
            phoneIndic = v
          }
        "
        @blur="$v.phone.$touch()"
        :placeholder="$t('Phone N°') + ' *'"
        :validations="[
          {
            condition: !$v.phone.required && $v.phone.$error,
            text: $t('Field is required.')
          },
          {
            condition: $v.phoneIndic.$error && $v.phoneIndic.$error,
            text: $t('Phone extension is required.')
          }
        ]"
        :required="true"
      />

      <div class="form-heading">{{ $t('Personal information') }}</div>
      <base-select
        id="title"
        name="title"
        v-model="title"
        :placeholder="$t('Title') + ' *'"
        :validations="[
          {
            condition: !$v.title.required && $v.title.$error,
            text: $t('Field is required.')
          }
        ]"
        :required="true"
        autocomplete="honorific-prefix"
        :options="[
          { value: 'mr', label: $t('Mr.') },
          { value: 'mrs', label: $t('Mrs.') }
        ]"
      />
      <template v-if="currentCountry === 'JP'">
        <base-input
          type="text"
          id="kanjiLastName"
          name="kanjiLastName"
          v-model="kanjiLastName"
          @blur="$v.kanjiLastName.$touch()"
          :placeholder="$t('Local last name')"
        />
        <base-input
          type="text"
          id="kanjiFirstName"
          name="kanjiFirstName"
          v-model="kanjiFirstName"
          @blur="$v.kanjiFirstName.$touch()"
          :placeholder="$t('Local first name')"
        />
        <base-input
          type="text"
          id="lastname"
          name="lastname"
          autocomplete="family-name"
          v-model="lastname"
          @blur="$v.lastname.$touch()"
          :placeholder="$t('Last name') + ' *'"
          :validations="[
            {
              condition: !$v.lastname.required && $v.lastname.$error,
              text: $t('Field is required.')
            }
          ]"
          :required="true"
        />
        <base-input
          id="name"
          type="text"
          name="firstname"
          autocomplete="given-name"
          v-model="firstname"
          @blur="$v.firstname.$touch()"
          :placeholder="$t('First name') + ' *'"
          :validations="[
            {
              condition: !$v.firstname.required && $v.firstname.$error,
              text: $t('Field is required.')
            }
          ]"
          :required="true"
        />
      </template>
      <template v-else>
        <base-input
          id="name"
          type="text"
          name="firstname"
          autocomplete="given-name"
          v-model="firstname"
          @blur="$v.firstname.$touch()"
          :placeholder="$t('First name') + ' *'"
          :validations="[
            {
              condition: !$v.firstname.required && $v.firstname.$error,
              text: $t('Field is required.')
            }
          ]"
          :required="true"
        />
        <base-input
          type="text"
          id="lastname"
          name="lastname"
          autocomplete="family-name"
          v-model="lastname"
          @blur="$v.lastname.$touch()"
          :placeholder="$t('Last name') + ' *'"
          :validations="[
            {
              condition: !$v.lastname.required && $v.lastname.$error,
              text: $t('Field is required.')
            }
          ]"
          :required="true"
        />
      </template>

      <div class="toggle-line">
        <Toggle v-model="is_subscribed" id="is_subscribed"></Toggle>
        <label class="-small" for="is_subscribed">{{
          $t('I would like to sign up for the newsletter')
        }}</label>
      </div>

      <div
        class="toggle-line"
        :class="{ '-error': !$v.conditions.required && $v.conditions.$error }"
      >
        <Toggle v-model="conditions" id="conditions"></Toggle>
        <NoSSR>
          <label
            class="-small"
            for="conditions"
            v-html="
              $t('I accept the terms of use', {
                href: localizedUrl + '/page/terms'
              })
            "
            >*</label
          >
        </NoSSR>
        <div class="form-error">
          <span>{{ $t('You must accept the terms and conditions.') }}</span>
        </div>
      </div>
      <div
        class="recaptcha"
        v-html="
          $t(
            'This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.',
            {
              hrefPrivacy: 'https://policies.google.com/privacy',
              hrefTerms: 'https://policies.google.com/terms'
            }
          )
        "
      ></div>
      <button class="btn -block">{{ $t('Send') }}</button>
    </form>
  </transition>
</template>

<script>
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import { getAkamaiPosition, newsletter } from '../../utilities'
import BaseInput from './../form/BaseInput'
import BasePhone from './../form/BasePhone'
import BaseSelect from './../form/BaseSelect'
import BaseTextarea from './../form/BaseTextarea'
import BaseRadiobutton from '../form/BaseRadiobutton'
import NoSSR from 'src/themes/zenith/node_modules/vue-no-ssr'
import { email, required, requiredIf, sameAs } from 'vuelidate/lib/validators'
import { mapActions, mapGetters } from 'vuex'
import Toggle from '../form/Toggle'
import { ReadyHook } from 'vue-asyncready'
import { TaskQueue } from '@vue-storefront/core/lib/sync'
import config from 'config'
import { storeOfficialBoutiqueCountries, storeLocator } from '../../utilities/store-locator'
import moment from 'moment'

export default {
  name: 'ContactStoreForm',
  mixins: [ReadyHook],
  props: ['watchRef'],
  components: {
    BaseRadiobutton,
    BaseInput,
    BasePhone,
    Toggle,
    BaseSelect,
    BaseTextarea,
    NoSSR
  },
  data: () => ({
    countries: [],
    firstname: '',
    lastname: '',
    kanjiFirstName: '',
    kanjiLastName: '',
    title: '',
    country: '',
    reason: 'appointment',
    when: new Date(),
    date: '',
    appointment: '',
    conditions: false,
    message: '',
    getback: '',
    email: '',
    phone: '',
    wechat: '',
    phoneIndic: '',
    formOpen: false,
    is_subscribed: false,
    toggleOpeningHour: false,
    stores: [],
    selectedStore: null,
    storeOptions: [],
    store: '',
    success: false
  }),
  computed: {
    localizedUrl() {
      return currentStoreView().url || ''
    },
    currentUser() {
      if (this.isLoggedIn) return this.$store.state.user.current
      else {
        return null
      }
    },
    currentCountry() {
      return currentStoreView().i18n.defaultCountry
    }
  },
  validations: {
    title: {
      required
    },
    firstname: {
      required
    },
    lastname: {
      required
    },
    conditions: {
      sameAs: sameAs(() => true)
    },
    reason: {
      required
    },
    country: {
      required
    },
    store: {
      required
    },
    phone: {
      required
    },
    phoneIndic: {
      required
    },
    email: {
      required,
      email
    },
    date: {
      sameAs: sameAs(function() {
        if (this.date && this.reason === 'appointment') {
          const day = moment(this.date)
            .format('dddd')
            .toLowerCase()
          const matchingDay = this.selectedStore.opening.filter(
            item => item.day === day && item.slots.length
          )
          this.$v.date.$touch()
          return matchingDay.length ? this.date : false
        } else {
          this.$v.date.$touch()
          return this.date
        }
      })
    },
    wechat: {
      required: requiredIf(function() {
        return this.getback === 'wechat'
      })
    }
  },
  watch: {
    store() {
      this.selectedStore = this.stores.find(
        store => store.seller_code === this.store
      )
    },
    country() {
      if (this.country) {
        storeLocator({ country: this.country }).then(stores => {
          this.stores = stores.filter(
            store => store.is_active && store.type === 'Official boutique' && store.showForm
          )
          this.storeOptions = this.stores.map(store => {
            return {
              value: store.seller_code,
              label: `${store.name}${store.address.street ? `(${store.address.street})` : ''}`
            }
          })
        })
      }
    }
  },
  methods: {
    ...mapActions('feedback', ['show']),
    ...mapGetters('user', ['isLoggedIn']),
    getCountries() {
      storeOfficialBoutiqueCountries().then(res => {
        this.countries = res.items.map(item => {
          return { value: item.code, label: item.name }
        })
      })
    },
    async register() {
      if (this.$v.$invalid) {
        this.$v.$touch()
        setTimeout(() => {
          let bodyRect = document.body.getBoundingClientRect()
          let elem = document.querySelector('.-error')
          if (elem) {
            elem.querySelector('select, input').focus()
            let elemRect = elem.getBoundingClientRect()
            let offset = elemRect.top - bodyRect.top - 140
            window.scrollTo({
              top: offset,
              behavior: 'smooth'
            })
          }
        }, 1)
      } else {
        let form = {
          seller_code: this.selectedStore.seller_code,
          firstname: this.firstname,
          lastname: this.lastname,
          kanjiFirstName: this.kanjiFirstName,
          kanjiLastName: this.kanjiLastName,
          title: this.title,
          country: this.country,
          reason: this.reason,
          when: this.when,
          date: this.date,
          wechat: this.wechat,
          conditions: this.conditions,
          message: this.message,
          getback: this.getback,
          email: this.email,
          phone: this.phoneIndic + this.phone,
          is_subscribed: this.is_subscribed,
          type: this.appointment ? this.appointment : '-',
          c_type: 'store',
          c_customerID: this.currentUser ? this.currentUser.id : '',
          locale: currentStoreView().storeCode,
          watchRef: this.watchRef
        }

        await this.$store.dispatch('googleRecaptcha/isVerifiedCaptcha')
        if (!this.$store.state.googleRecaptcha.is_verified) {
          this.show({
            message: 'Recaptcha error. Please try again.',
            context: 'error'
          })
        } else {
          let position = await getAkamaiPosition()
          form.country = position.country

          TaskQueue.execute({
            url: config.api.url + '/api/ext/store-locator/contact',
            payload: {
              method: 'POST',
              mode: 'cors',
              credentials: 'include',
              headers: {
                Accept: 'application/json, text/plain, */*',
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(form)
            }
          }).then(() => {
            if (this.is_subscribed) {
              newsletter({
                email: this.email,
                civility: this.title,
                firstname: this.firstname,
                lastname: this.lastname,
                country: currentStoreView().storeCode
              }).then(() => {
                this.$gtm.trackEvent({
                  event: 'newsletterSubscription',
                  siteLocation: 'Store Locator page'
                })
              })
            }
          })
          this.success = true
        }
      }
    },
    keyup(e) {
      if (e.keyCode === 27) {
        this.$emit('close')
      }
    }
  },
  mounted() {
    this.$v.$reset()
    if (this.currentUser) {
      this.title = this.currentUser.prefix || ''
      this.firstname = this.currentUser.firstname || ''
      this.lastname = this.currentUser.lastname || ''
      this.email = this.currentUser.email || ''
      this.country = this.currentUser.custom_attributes.filter(
        i => i.attribute_code === 'country_id'
      ).length
        ? this.currentUser.custom_attributes.filter(
            i => i.attribute_code === 'country_id'
          )[0].value
        : this.country
        ? this.country
        : ''
      this.phone = this.currentUser.custom_attributes.filter(
        i => i.attribute_code === 'phone'
      ).length
        ? this.currentUser.custom_attributes.filter(
            i => i.attribute_code === 'phone'
          )[0].value
        : ''
    }
  },
  destroyed() {
    document.removeEventListener('keyup', this.keyup)
    document.removeEventListener('transitionend', this.transitionend)
  },
  created() {
    this.getCountries()
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/core';
.error-msg {
  color: #ff0000;
  margin-bottom: 2rem;
}
.no-openings {
  border-top: 1px solid rgba(#979797, 0.3);
  padding: 2rem 0;
}

.title {
  text-align: center;
  margin-bottom: 1rem;
  text-transform: uppercase;

  h3 {
    font-size: em(20);
    letter-spacing: em(2, 20);
    line-height: em(30, 20);
  }
  span {
    display: block;
    margin-bottom: 0.5rem;
  }
  strong {
    display: block;
    font-weight: bold;
    color: var(--zenith-theme-primary);
  }
}

.opening-hours {
  text-align: left;
  opacity: 0;
  max-height: 0;
  overflow: hidden;
  transition: all 0.2s ease-in-out;
  padding: 0;
  margin: 0;
  list-style: none;

  &.-open {
    opacity: 1;
    max-height: 21rem;
    margin-bottom: 3rem;
  }

  li {
    display: flex;
    padding: 0 0 1rem;
    justify-content: space-between;

    strong {
      margin-right: auto;
    }
    span {
      &:not(:last-child):after {
        content: '/';
        display: inline-block;
        padding: 0 1rem;
      }
    }
  }

  &-btn {
    width: 100%;
    position: relative;
    font-size: em(14);
    font-weight: 500;
    line-height: em(70, 14);
    letter-spacing: em(1, 14);
    background: none;
    border: none;
    margin: 0;
    padding: 0;
    text-align: left;
    text-transform: uppercase;
    border-top: 1px solid rgba(#979797, 0.3);

    &[aria-expanded='true']:after {
      height: 0.1rem;
      top: 3.4rem;
    }

    &:before {
      display: block;
      content: '';
      position: absolute;
      right: 0;
      top: 3.4rem;
      width: 1.3rem;
      height: 0.1rem;
      background: #000;
      transition: all 0.2s ease-in-out;
    }

    &:after {
      display: block;
      content: '';
      position: absolute;
      right: 0.6rem;
      top: 2.8rem;
      height: 1.3rem;
      width: 0.1rem;
      background: #000;
      transition: all 0.2s ease-in-out;
    }
  }
}

.form {
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  padding: 14rem 4rem 8rem 4rem;

  &::-webkit-scrollbar {
    width: 2px;
    height: 2px;
    border-radius: 2px;
    background-color: #dfdfdf;
  }

  &::-webkit-scrollbar-thumb {
    background: #262626;
    border-radius: 2px;
  }

  @include bp(max md) {
    padding: 8rem 1.5rem 2rem 1.5rem;
  }

  &.-open {
    opacity: 1;
    pointer-events: all;
    transform: translateX(-100%);

    @include bp(max md) {
      transform: translateX(0);
    }
  }

  .title {
    margin-bottom: 4rem;

    &:before {
      content: '★';
      display: block;
      text-align: center;
      font-size: em(20);
      margin-bottom: 0.6rem;
    }
  }

  .recaptcha {
    font-size: em(12);
    margin-bottom: 4rem;
    /deep/ a {
      color: var(--zenith-theme-primary);
      text-decoration: underline;
    }
  }
}
</style>
